<template>
  <section class="lead-communication-history">
    <header class="lead-communication-history__heading">
      <div>
        <strong>Communication history</strong>
        <span>Latest activity across all channels</span>
      </div>
      <AppButton design="new" theme="dark" type="button" @click.native="openHistory">
        Full history
        <span aria-hidden="true">→</span>
      </AppButton>
    </header>

    <LeadCommunicationSuggestions
      ref="suggestions"
      :lead-id="leadId"
      @accepted="handleSuggestionAccepted"
      @pending-change="setPending('suggestions', $event)"
    />

    <div class="lead-communication-history__feed lead-communication-history__feed--preview">
      <LeadCommunicationTimeline
        :items="previewItems"
        :loading="flat.loading"
        :error="flat.error"
        :has-more="false"
        @retry="loadFlat(true)"
      />
    </div>

    <footer v-if="totalMessageCount" class="lead-communication-history__preview-footer">
      <span>
        Showing {{ previewItems.length }} latest
        <template v-if="remainingMessageCount">· {{ remainingMessageCount }} more</template>
      </span>
    </footer>

    <LeadFlowManualCommunicationForm
      ref="manualCommunicationForm"
      :lead-id="leadId"
      :communication-methods="communicationMethods"
      :editing="editingCommunication"
      @saved="handleCommunicationSaved"
      @cancel-edit="editingCommunication = null"
      @pending-change="setPending('manual-form', $event)"
    />

    <LeadCommunicationHistoryDrawer
      v-if="historyOpen"
      :lead-id="leadId"
      :tabs="tabs"
      :active-tab="activeTab"
      :flat="flat"
      :active-grouped="activeGrouped"
      :group-refresh-key="groupRefreshKeys[activeTab] || 0"
      :pending-ids="mutationPendingIds"
      @close="closeHistory"
      @select="selectTab"
      @retry-flat="loadFlat(true)"
      @load-more-flat="loadFlat(false)"
      @retry-grouped="loadGrouped(activeTab, true)"
      @load-more-grouped="loadGrouped(activeTab, false)"
      @edit="editFromHistory"
      @delete="deleteCommunication"
    />
  </section>
</template>

<script>
import {
  deleteLeadFlowCommunication,
  getLeadFlowCommunicationSummary,
  getLeadFlowCommunications,
  getLeadFlowGroupedCommunications
} from '@/http'
import LeadCommunicationSuggestions from '@/views/crm/task-tracker/LeadCommunicationSuggestions.vue'
import LeadCommunicationHistoryDrawer from '@/views/crm/task-tracker/LeadCommunicationHistoryDrawer.vue'
import LeadCommunicationTimeline from '@/views/crm/task-tracker/LeadCommunicationTimeline.vue'
import LeadFlowManualCommunicationForm from '@/views/crm/task-tracker/LeadFlowManualCommunicationForm.vue'
import {
  createLeadContext,
  isActiveLeadContext
} from '@/views/crm/task-tracker/leadFlowContext'

const CHANNELS = ['all', 'trengo', 'email', 'other']
const PREVIEW_MESSAGE_COUNT = 6

const emptyGroupedState = () => ({
  loaded: false,
  loading: false,
  loadingMore: false,
  error: null,
  generation: 0,
  summary: {
    total_messages: 0,
    total_groups: 0,
    by_method: []
  },
  groups: {
    results: [],
    nextCursor: null,
    hasMore: false
  }
})

const createGroupedStates = () => ({
  all: emptyGroupedState(),
  trengo: emptyGroupedState(),
  email: emptyGroupedState(),
  other: emptyGroupedState()
})

export default {
  name: 'LeadCommunicationHistory',
  components: {
    LeadCommunicationSuggestions,
    LeadCommunicationHistoryDrawer,
    LeadCommunicationTimeline,
    LeadFlowManualCommunicationForm
  },
  props: {
    leadId: {
      type: Number,
      required: true
    },
    communicationMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeTab: 'timeline',
      flat: {
        results: [],
        nextCursor: null,
        hasMore: false,
        loading: false,
        loadingMore: false,
        error: null,
        generation: 0
      },
      channelSummary: {
        loaded: false,
        loading: false,
        error: null,
        generation: 0,
        totalMessages: 0,
        channels: {}
      },
      grouped: createGroupedStates(),
      groupRefreshKeys: {
        all: 0,
        trengo: 0,
        email: 0,
        other: 0
      },
      editingCommunication: null,
      historyOpen: false,
      mutationPendingIds: {},
      pendingSections: {},
      contextGeneration: 0
    }
  },
  computed: {
    previewItems() {
      return this.flat.results.slice(0, PREVIEW_MESSAGE_COUNT)
    },
    totalMessageCount() {
      if (this.channelSummary.loaded) return this.channelSummary.totalMessages
      return this.flat.results.length
    },
    remainingMessageCount() {
      return Math.max(this.totalMessageCount - this.previewItems.length, 0)
    },
    activeGrouped() {
      return this.grouped[this.activeTab] || emptyGroupedState()
    },
    tabs() {
      let timelineCount = null
      if (this.channelSummary.loaded) {
        timelineCount = this.channelSummary.totalMessages
      } else if (!this.flat.loading) {
        timelineCount = this.flat.results.length
      }
      return [
        {
          code: 'timeline',
          name: 'Timeline',
          count: timelineCount
        },
        ...CHANNELS.map(channel => {
          let count = null
          if (this.channelSummary.loaded) {
            count = this.channelSummary.channels[channel]
          }
          if (typeof count === 'undefined') count = null
          if (count === null && this.grouped[channel].loaded) {
            count = Number(this.grouped[channel].summary.total_messages) || 0
          }
          return {
            code: channel,
            name: this.channelName(channel),
            count
          }
        })
      ]
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.loadFlat(true)
        this.loadChannelSummary()
      }
    }
  },
  beforeDestroy() {
    this.contextGeneration += 1
    this.flat.generation += 1
    this.channelSummary.generation += 1
    CHANNELS.forEach(channel => {
      this.grouped[channel].generation += 1
    })
  },
  methods: {
    async loadChannelSummary() {
      const leadId = this.leadId
      const generation = this.channelSummary.generation + 1
      this.channelSummary = {
        ...this.channelSummary,
        loading: true,
        error: null,
        generation
      }
      try {
        const { data } = await getLeadFlowCommunicationSummary(leadId)
        if (generation !== this.channelSummary.generation || leadId !== this.leadId) return
        const channels = (data.channels || []).reduce((counts, item) => {
          const code = item.channel && item.channel.code
          if (code) counts[code] = Number(item.message_count) || 0
          return counts
        }, {})
        this.channelSummary = {
          loaded: true,
          loading: false,
          error: null,
          generation,
          totalMessages: Number(data.total_messages) || 0,
          channels
        }
      } catch (error) {
        if (generation === this.channelSummary.generation && leadId === this.leadId) {
          this.channelSummary = {
            ...this.channelSummary,
            loading: false,
            error
          }
        }
      }
    },
    async loadFlat(reset) {
      if (reset ? this.flat.loading : this.flat.loadingMore) return
      if (!reset && (!this.flat.hasMore || !this.flat.nextCursor)) return

      const leadId = this.leadId
      const generation = reset ? this.flat.generation + 1 : this.flat.generation
      this.flat.generation = generation
      this.flat.loading = reset
      this.flat.loadingMore = !reset
      this.flat.error = null
      try {
        const params = { page_size: 30 }
        if (!reset) params.cursor = this.flat.nextCursor
        const { data } = await getLeadFlowCommunications(leadId, params)
        if (generation !== this.flat.generation || leadId !== this.leadId) return
        this.flat.results = this.mergeById(
          reset ? [] : this.flat.results,
          data.results || []
        )
        this.flat.nextCursor = data.next_cursor || null
        this.flat.hasMore = Boolean(data.has_more && this.flat.nextCursor)
      } catch (error) {
        if (generation === this.flat.generation && leadId === this.leadId) {
          this.flat.error = error
        }
      } finally {
        if (generation === this.flat.generation && leadId === this.leadId) {
          this.flat.loading = false
          this.flat.loadingMore = false
        }
      }
    },
    async loadGrouped(channel, reset) {
      const state = this.grouped[channel]
      if (!state || (reset ? state.loading : state.loadingMore)) return
      if (!reset && (!state.groups.hasMore || !state.groups.nextCursor)) return

      const leadId = this.leadId
      const generation = reset ? state.generation + 1 : state.generation
      const nextState = {
        ...state,
        generation,
        loading: reset,
        loadingMore: !reset,
        error: null
      }
      this.$set(this.grouped, channel, nextState)
      try {
        const params = {
          channel,
          group_page_size: 10,
          messages_page_size: 30
        }
        if (!reset) params.cursor = state.groups.nextCursor
        const { data } = await getLeadFlowGroupedCommunications(leadId, params)
        const currentState = this.grouped[channel]
        if (generation !== currentState.generation || leadId !== this.leadId) return
        const incomingGroups = data.groups && data.groups.results ? data.groups.results : []
        const results = this.mergeGroups(
          reset ? [] : currentState.groups.results,
          incomingGroups
        )
        this.$set(this.grouped, channel, {
          ...currentState,
          loaded: true,
          loading: false,
          loadingMore: false,
          error: null,
          summary: data.summary || currentState.summary,
          groups: {
            results,
            nextCursor: data.groups && data.groups.next_cursor ? data.groups.next_cursor : null,
            hasMore: Boolean(data.groups && data.groups.has_more && data.groups.next_cursor)
          }
        })
        if (reset) this.$set(this.groupRefreshKeys, channel, this.groupRefreshKeys[channel] + 1)
      } catch (error) {
        const currentState = this.grouped[channel]
        if (generation === currentState.generation && leadId === this.leadId) {
          this.$set(this.grouped, channel, {
            ...currentState,
            loading: false,
            loadingMore: false,
            error
          })
        }
      }
    },
    selectTab(channel) {
      this.activeTab = channel
      if (CHANNELS.includes(channel) && !this.grouped[channel].loaded) {
        this.loadGrouped(channel, true)
      }
    },
    openHistory() {
      this.activeTab = 'timeline'
      this.historyOpen = true
    },
    closeHistory() {
      this.historyOpen = false
    },
    editFromHistory(communication) {
      this.closeHistory()
      this.editingCommunication = communication
      this.$nextTick(() => {
        const form = this.$refs.manualCommunicationForm
        if (form && form.$el) form.$el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    async handleCommunicationSaved(result) {
      this.editingCommunication = null
      await Promise.all([
        this.loadFlat(true),
        this.loadChannelSummary(),
        this.refreshLoadedGroups()
      ])
      if (result.item && result.item.date) this.$emit('last-activity', result.item.date)
      if (result.mode === 'create' && (result.direction || this.directionCode(result.item)) === 'inbound') {
        this.$emit('inbound-communication', result.item)
      }
      this.$notify({
        type: 'success',
        title: result.mode === 'edit' ? 'Communication updated' : 'Communication added'
      })
    },
    async deleteCommunication(id) {
      if (this.mutationPendingIds[id]) return
      const context = createLeadContext(this.leadId, this.contextGeneration)
      if (!context.leadId) return
      this.$set(this.mutationPendingIds, id, true)
      this.setPending(`delete:${id}`, true)
      try {
        await deleteLeadFlowCommunication(context.leadId, id)
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        this.flat.results = this.flat.results.filter(item => item.id !== id)
        await Promise.all([
          this.loadChannelSummary(),
          this.refreshLoadedGroups()
        ])
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        if (this.editingCommunication && this.editingCommunication.id === id) {
          this.editingCommunication = null
        }
        this.$notify({ type: 'success', title: 'Communication deleted' })
      } catch (error) {
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        const data = error.response && error.response.data
        this.$notify({
          type: 'error',
          title: data && data.message ? data.message : 'Unable to delete communication'
        })
      } finally {
        if (isActiveLeadContext(context, this.leadId, this.contextGeneration)) {
          this.$delete(this.mutationPendingIds, id)
          this.setPending(`delete:${id}`, false)
        }
      }
    },
    async handleSuggestionAccepted(data) {
      await Promise.all([
        this.loadFlat(true),
        this.loadChannelSummary(),
        this.refreshLoadedGroups()
      ])
      this.$emit('suggestion-accepted', data)
    },
    refreshAll() {
      return Promise.all([
        this.loadFlat(true),
        this.loadChannelSummary(),
        this.refreshLoadedGroups(),
        this.refreshSuggestions()
      ])
    },
    refreshSuggestions() {
      const suggestions = this.$refs.suggestions
      return suggestions ? suggestions.loadSuggestions() : Promise.resolve()
    },
    refreshLoadedGroups() {
      const requests = CHANNELS
        .filter(channel => this.grouped[channel].loaded)
        .map(channel => this.loadGrouped(channel, true))
      return Promise.all(requests)
    },
    mergeById(current, incoming) {
      const seen = new Set(current.map(item => item.id))
      return current.concat(incoming.filter(item => {
        if (!item || seen.has(item.id)) return false
        seen.add(item.id)
        return true
      }))
    },
    mergeGroups(current, incoming) {
      const seen = new Set(current.map(item => item.group && item.group.key))
      return current.concat(incoming.filter(item => {
        const key = item && item.group && item.group.key
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
      }))
    },
    channelName(channel) {
      return {
        all: 'All',
        trengo: 'Trengo',
        email: 'Email',
        other: 'Other'
      }[channel] || 'Communication'
    },
    directionCode(item) {
      const direction = item && item.direction
      return typeof direction === 'string' ? direction : (direction && direction.code) || ''
    },
    setPending(section, pending) {
      if (pending) this.$set(this.pendingSections, section, true)
      else this.$delete(this.pendingSections, section)
      this.$emit('pending-change', Object.keys(this.pendingSections).length > 0)
    },
    reset() {
      this.contextGeneration += 1
      const nextGroupedGenerations = CHANNELS.reduce((generations, channel) => {
        generations[channel] = this.grouped[channel].generation + 1
        return generations
      }, {})
      this.activeTab = 'timeline'
      this.flat = {
        results: [],
        nextCursor: null,
        hasMore: false,
        loading: false,
        loadingMore: false,
        error: null,
        generation: this.flat.generation + 1
      }
      this.channelSummary = {
        loaded: false,
        loading: false,
        error: null,
        generation: this.channelSummary.generation + 1,
        totalMessages: 0,
        channels: {}
      }
      this.grouped = createGroupedStates()
      CHANNELS.forEach(channel => {
        this.grouped[channel].generation = nextGroupedGenerations[channel]
      })
      this.groupRefreshKeys = {
        all: 0,
        trengo: 0,
        email: 0,
        other: 0
      }
      this.editingCommunication = null
      this.historyOpen = false
      this.mutationPendingIds = {}
      this.pendingSections = {}
      this.$emit('pending-change', false)
    }
  }
}
</script>

<style scoped>
.lead-communication-history {
  display: grid;
  min-width: 0;
  align-content: start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #dfe4dd;
  border-radius: 7px;
  background: #fff;
}

.lead-communication-history__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.lead-communication-history__heading > div {
  display: grid;
  gap: 4px;
}

.lead-communication-history__heading strong {
  color: #657574;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-communication-history__heading > div > span {
  color: #8b9694;
  font-size: 10px;
}

.lead-communication-history__heading > button {
  display: inline-flex;
  min-height: 34px;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid #b9d2f4;
  border-radius: 6px;
  background: #f4f8fe;
  color: #1769e0;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.lead-communication-history__heading > button:hover {
  border-color: #8eb8ef;
  background: #eaf2fd;
}

.lead-communication-history__feed {
  min-height: 280px;
  max-height: 560px;
  padding-right: 5px;
  overflow-y: auto;
  scrollbar-color: #a7b59d transparent;
  scrollbar-width: thin;
}

.lead-communication-history__feed--preview {
  min-height: 0;
  max-height: none;
  padding-right: 0;
  overflow: visible;
}

.lead-communication-history__preview-footer {
  display: flex;
  min-height: 38px;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid #dfe6ef;
  border-radius: 6px;
  background: #f7f9fc;
}

.lead-communication-history__preview-footer > span {
  color: #7b898f;
  font-size: 10px;
}

.lead-communication-history__groups {
  display: grid;
  gap: 10px;
}

.lead-communication-history__more {
  min-height: 31px;
  border: 1px solid #d7e2cb;
  border-radius: 5px;
  background: #f8fbf5;
  color: #5c8d20;
  font-size: 9px;
  font-weight: 700;
  cursor: pointer;
}

.lead-communication-history__empty,
.lead-communication-history__state,
.lead-communication-history__append-error {
  margin: 10px 0;
  color: #8a9695;
  font-size: 10px;
}

.lead-communication-history__state button,
.lead-communication-history__append-error button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #55940a;
  font-weight: 700;
  cursor: pointer;
}

.lead-communication-history__append-error {
  color: #b85049;
  text-align: center;
}

.lead-communication-history__loading {
  display: grid;
  gap: 9px;
}

.lead-communication-history__loading span {
  height: 100px;
  border-radius: 6px;
  background: #eef1ec;
}
</style>
